import { useState, useCallback } from 'react';
import { TableOrder } from './types';

// ----------------------------------------------------------------------

export interface UseTableProps {
  defaultDense?: boolean;
  defaultOrderBy?: string;
  defaultOrder?: TableOrder;
  defaultCurrentPage?: number;
  defaultRowsPerPage?: number;
  defaultSelected?: string[];
}

export function useTable(props?: UseTableProps) {
  const [dense, setDense] = useState(props?.defaultDense ?? false);
  const [orderBy, setOrderBy] = useState(props?.defaultOrderBy ?? 'name');
  const [order, setOrder] = useState<TableOrder>(props?.defaultOrder ?? 'asc');
  const [page, setPage] = useState(props?.defaultCurrentPage ?? 0);
  const [rowsPerPage, setRowsPerPage] = useState(props?.defaultRowsPerPage ?? 10);
  const [selected, setSelected] = useState<string[]>(props?.defaultSelected ?? []);

  const onSort = useCallback(
    (id: string) => {
      const isAsc = orderBy === id && order === 'asc';
      if (id !== '') {
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(id);
      }
    },
    [order, orderBy]
  );

  const onSelectRow = useCallback(
    (id: string) => {
      const selectedIndex = selected.indexOf(id);
      let newSelected: string[] = [];

      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, id);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1));
      } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selected.slice(0, selectedIndex),
          selected.slice(selectedIndex + 1)
        );
      }

      setSelected(newSelected);
    },
    [selected]
  );

  const onSelectAllRows = useCallback((checked: boolean, newSelecteds: string[]) => {
    if (checked) {
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  }, []);

  const onChangePage = useCallback((_event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const onChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    []
  );

  const onChangeDense = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setDense(event.target.checked);
  }, []);

  const onResetPage = useCallback(() => {
    setPage(0);
  }, []);

  return {
    dense,
    order,
    page,
    orderBy,
    rowsPerPage,
    selected,
    setPage,
    setDense,
    onSort,
    onSelectRow,
    setSelected,
    onChangePage,
    onChangeDense,
    onResetPage,
    onSelectAllRows,
    onChangeRowsPerPage
  };
}
